import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { currentRole, isLivyAdmin, livyUnavailableMessage } from 'src/components/livy/access';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, ListItemButton, MenuItem, Skeleton, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { LivyMarkdown } from 'src/components/livy/LivyMarkdown';
import { livyConfig } from 'src/lib/livyTransport';
import { livyJson } from 'src/lib/livyApi';
const AUDIENCES = [{
  value: 'public',
  label: 'Public',
  help: 'Anyone, including the marketing site'
}, {
  value: 'authed',
  label: 'Customers',
  help: 'Signed-in property managers'
}, {
  value: 'admin',
  label: 'Livable staff',
  help: 'Internal only'
}];
const BLANK = {
  slug: '',
  title: '',
  body: '',
  audience: 'authed',
  tags: []
};

/**
 * The knowledge base is what the assistant quotes when asked how Livable works, so editing an
 * article changes what every surface answers. Audience is an access boundary, not a label: the
 * server filters reads by role, and this page only decides what to send.
 */
export function LivyKbPage() {
  var _AUDIENCES$find;
  const isAdmin = isLivyAdmin(currentRole());
  const configured = Boolean(livyConfig());
  const _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    articles = _useState2[0],
    setArticles = _useState2[1];
  // Without this the three groups all render "Nothing yet" during the first fetch, so the page
  // reads as an empty knowledge base rather than one that has not loaded.
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    loaded = _useState4[0],
    setLoaded = _useState4[1];
  const _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    filter = _useState6[0],
    setFilter = _useState6[1];
  const _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    draft = _useState8[0],
    setDraft = _useState8[1];
  const _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    status = _useState0[0],
    setStatus = _useState0[1];
  const _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    busy = _useState10[0],
    setBusy = _useState10[1];
  const _useState11 = useState('write'),
    _useState12 = _slicedToArray(_useState11, 2),
    tab = _useState12[0],
    setTab = _useState12[1];
  // Delete sits next to Save and the API has no undo. The corpus here is what the assistant quotes
  // to customers, so a mis-click is a wrong answer in production, not just a lost draft.
  const _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    confirmingDelete = _useState14[0],
    setConfirmingDelete = _useState14[1];
  const load = useCallback(async () => {
    try {
      const _await$livyJson = await livyJson('/kb'),
        list = _await$livyJson.articles;
      setArticles(list);
    } catch (e) {
      setStatus({
        kind: 'error',
        text: e.message
      });
    } finally {
      setLoaded(true);
    }
  }, []);
  useEffect(() => {
    if (isAdmin && configured) void load();
  }, [isAdmin, configured, load]);
  const open = async slug => {
    setStatus(null);
    try {
      const _await$livyJson2 = await livyJson("/kb/".concat(slug)),
        article = _await$livyJson2.article;
      setDraft(article);
      setTab('write');
    } catch (e) {
      setStatus({
        kind: 'error',
        text: e.message
      });
    }
  };

  // The palette links a knowledge hit as /livy/kb?article=<slug>. Without this the link landed on
  // the blank editor, so a result that looked like a destination was not one.
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get('article');
    if (isAdmin && configured && loaded && wanted) void open(wanted);
    // Read once, on arrival, after the list has loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, configured, loaded]);

  // HubSpot stays where public articles are written, so this pulls their current text forward
  // rather than making this page a second place to edit them.
  const sync = async () => {
    setBusy(true);
    setStatus(null);
    try {
      const r = await livyJson('/kb/sync', {
        method: 'POST',
        body: {}
      });
      setStatus({
        kind: 'ok',
        text: "".concat(r.written, " updated, ").concat(r.unchanged, " already current, ").concat(r.skipped.length, " left alone because someone wrote them here.")
      });
      await load();
    } catch (e) {
      setStatus({
        kind: 'error',
        text: e.message
      });
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setStatus(null);
    try {
      const _await$livyJson3 = await livyJson("/kb/".concat(draft.slug || ''), {
          method: 'PUT',
          body: draft
        }),
        article = _await$livyJson3.article;
      setDraft(article);
      setStatus({
        kind: 'ok',
        text: "Saved. The assistant will quote this within the hour."
      });
      await load();
    } catch (e) {
      setStatus({
        kind: 'error',
        text: e.message
      });
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!(draft !== null && draft !== void 0 && draft.slug)) return;
    setConfirmingDelete(false);
    setBusy(true);
    try {
      await livyJson("/kb/".concat(draft.slug), {
        method: 'DELETE'
      });
      setDraft(null);
      await load();
    } catch (e) {
      setStatus({
        kind: 'error',
        text: e.message
      });
    } finally {
      setBusy(false);
    }
  };
  const grouped = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const match = x => !needle || x.title.toLowerCase().includes(needle) || x.slug.toLowerCase().includes(needle);
    return AUDIENCES.map(a => _objectSpread(_objectSpread({}, a), {}, {
      items: articles.filter(x => x.audience === a.value && match(x))
    }));
  }, [articles, filter]);
  if (!isAdmin || !configured) {
    return /*#__PURE__*/React.createElement(Box, {
      sx: {
        p: '24px'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body1"
    }, livyUnavailableMessage));
  }
  return /*#__PURE__*/React.createElement(Stack, {
    direction: {
      xs: 'column',
      md: 'row'
    },
    sx: {
      height: '100%',
      minHeight: 0
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: {
        xs: '100%',
        md: 300
      },
      flex: '0 0 auto',
      borderRight: {
        md: 1
      },
      borderColor: 'divider',
      overflowY: 'auto',
      p: '16px'
    }
  }, /*#__PURE__*/React.createElement(Button, {
    fullWidth: true,
    variant: "contained",
    onClick: () => {
      setDraft(_objectSpread({}, BLANK));
      setTab('write');
    },
    sx: {
      mb: '12px',
      textTransform: 'none'
    }
  }, "New article"), /*#__PURE__*/React.createElement(Button, {
    fullWidth: true,
    variant: "outlined",
    onClick: sync,
    disabled: busy,
    sx: {
      mb: '12px',
      textTransform: 'none'
    }
  }, busy ? /*#__PURE__*/React.createElement(CircularProgress, {
    size: 18
  }) : 'Sync from HubSpot'), /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    fullWidth: true,
    value: filter,
    onChange: e => setFilter(e.target.value),
    placeholder: "Filter by title or slug",
    sx: {
      mb: '14px'
    }
  }), !loaded && /*#__PURE__*/React.createElement(Stack, {
    spacing: "8px",
    "aria-label": "Loading articles"
  }, [0, 1, 2, 3, 4, 5].map(i => /*#__PURE__*/React.createElement(Skeleton, {
    key: i,
    variant: "rounded",
    height: 38
  }))), loaded && grouped.map(group => /*#__PURE__*/React.createElement(Box, {
    key: group.value,
    sx: {
      mb: '16px'
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    alignItems: "center",
    justifyContent: "space-between",
    sx: {
      mb: '6px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 11,
      letterSpacing: '.07em',
      textTransform: 'uppercase',
      color: 'text.secondary'
    }
  }, group.label), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 11,
      color: 'text.disabled',
      fontVariantNumeric: 'tabular-nums'
    }
  }, group.items.length)), group.items.length === 0 && /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, filter ? 'No match' : 'Nothing yet'), group.items.map(a =>
  /*#__PURE__*/
  // ListItemButton, not a Box with onClick: it is focusable, announced as a
  // button, and handles Enter and Space, none of which a div does.
  React.createElement(ListItemButton, {
    key: a.slug,
    onClick: () => void open(a.slug),
    selected: (draft === null || draft === void 0 ? void 0 : draft.slug) === a.slug,
    sx: {
      px: '10px',
      py: '8px',
      mb: '4px',
      borderRadius: '8px',
      display: 'block'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 13.5,
      fontWeight: 500
    }
  }, a.title), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 11.5,
      color: 'text.secondary'
    }
  }, a.slug)))))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      flex: 1,
      minWidth: 0,
      overflowY: 'auto',
      p: '20px'
    }
  }, status && /*#__PURE__*/React.createElement(Alert, {
    severity: status.kind === 'ok' ? 'success' : 'error',
    sx: {
      mb: '14px'
    }
  }, status.text), !draft ? /*#__PURE__*/React.createElement(Stack, {
    alignItems: "center",
    justifyContent: "center",
    spacing: "6px",
    sx: {
      height: '100%',
      minHeight: 280,
      color: 'text.secondary',
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 15,
      fontWeight: 600,
      color: 'text.primary'
    }
  }, loaded ? "".concat(articles.length, " articles") : 'Loading the knowledge base'), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      maxWidth: 380
    }
  }, "Pick one to edit, or start a new one. Audience decides who the assistant may quote it to.")) : /*#__PURE__*/React.createElement(Stack, {
    spacing: "14px",
    sx: {
      maxWidth: 820
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Title",
    value: draft.title,
    onChange: e => setDraft(_objectSpread(_objectSpread({}, draft), {}, {
      title: e.target.value
    })),
    size: "small",
    fullWidth: true
  }), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "12px"
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Slug",
    value: draft.slug,
    onChange: e => setDraft(_objectSpread(_objectSpread({}, draft), {}, {
      slug: e.target.value
    })),
    size: "small",
    helperText: "Left blank, it is derived from the title",
    sx: {
      flex: 1
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    select: true,
    label: "Audience",
    value: draft.audience,
    onChange: e => setDraft(_objectSpread(_objectSpread({}, draft), {}, {
      audience: e.target.value
    })),
    size: "small",
    sx: {
      width: 220
    },
    helperText: (_AUDIENCES$find = AUDIENCES.find(a => a.value === draft.audience)) === null || _AUDIENCES$find === void 0 ? void 0 : _AUDIENCES$find.help
  }, AUDIENCES.map(a => /*#__PURE__*/React.createElement(MenuItem, {
    key: a.value,
    value: a.value
  }, a.label)))), /*#__PURE__*/React.createElement(ToggleButtonGroup, {
    size: "small",
    exclusive: true,
    value: tab,
    onChange: (_, v) => v && setTab(v),
    sx: {
      alignSelf: 'flex-start'
    }
  }, /*#__PURE__*/React.createElement(ToggleButton, {
    value: "write",
    sx: {
      textTransform: 'none',
      px: '14px'
    }
  }, "Write"), /*#__PURE__*/React.createElement(ToggleButton, {
    value: "preview",
    sx: {
      textTransform: 'none',
      px: '14px'
    }
  }, "Preview")), tab === 'write' ? /*#__PURE__*/React.createElement(TextField, {
    multiline: true,
    minRows: 16,
    value: draft.body,
    onChange: e => setDraft(_objectSpread(_objectSpread({}, draft), {}, {
      body: e.target.value
    })),
    placeholder: "Markdown. Headings, lists and tables all render.",
    inputProps: {
      style: {
        fontFamily: 'monospace',
        fontSize: 13
      }
    }
  }) : /*#__PURE__*/React.createElement(Box, {
    sx: {
      border: 1,
      borderColor: 'divider',
      borderRadius: '10px',
      p: '16px',
      minHeight: 300
    }
  }, /*#__PURE__*/React.createElement(LivyMarkdown, {
    content: draft.body
  })), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "10px",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    onClick: save,
    disabled: busy || !draft.title,
    sx: {
      textTransform: 'none'
    }
  }, busy ? /*#__PURE__*/React.createElement(CircularProgress, {
    size: 18
  }) : 'Save'), draft.slug && /*#__PURE__*/React.createElement(Button, {
    color: "error",
    onClick: () => setConfirmingDelete(true),
    disabled: busy,
    sx: {
      textTransform: 'none'
    }
  }, "Delete"), draft.updatedAt && /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    variant: "outlined",
    label: "Updated ".concat(new Date(draft.updatedAt).toLocaleString())
  })))), /*#__PURE__*/React.createElement(Dialog, {
    open: confirmingDelete,
    onClose: () => setConfirmingDelete(false),
    maxWidth: "xs",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(DialogTitle, null, "Delete this article?"), /*#__PURE__*/React.createElement(DialogContent, null, /*#__PURE__*/React.createElement(DialogContentText, null, draft !== null && draft !== void 0 && draft.title ? "\"".concat(draft.title, "\" (").concat(draft.slug, ")") : draft === null || draft === void 0 ? void 0 : draft.slug, " will be removed. The assistant stops quoting it within the hour. This cannot be undone.")), /*#__PURE__*/React.createElement(DialogActions, null, /*#__PURE__*/React.createElement(Button, {
    onClick: () => setConfirmingDelete(false),
    sx: {
      textTransform: 'none'
    }
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    color: "error",
    variant: "contained",
    onClick: remove,
    sx: {
      textTransform: 'none'
    }
  }, "Delete"))));
}